import { useCallback, useEffect, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

/**
 * Makes a discarded form read as clean again.
 *
 * Call the returned function straight after `reset(...)` in a SaveFooter's `onDiscard`.
 *
 * **Why this is needed at all.** react-hook-form materialises a registered path. A brand with no loyalty
 * program arrives from the API as `loyaltyConfiguration: null`, the Loyalty tab registers
 * `loyaltyConfiguration.vendor`, and the form's value becomes `{ vendor: null }`. `reset(brand)` puts the
 * defaults back to `null`, registration recreates the object, and from then on the form's values can never
 * equal its own defaults. `isDirty` is a deep comparison, so it is stuck true; `dirtyFields` tracks actual
 * edits, so it stays empty.
 *
 * QA hit this as an editor that could not be left: the footer kept offering Cancel, pressing it re-ran the
 * discard, and `beforeunload` — which also reads `isDirty` — made the browser refuse to leave the page
 * (TTD-161, reopened). On load it looks fine only because `isDirty` is recomputed lazily.
 *
 * **The guard is the point.** Re-baselining is only done when the form claims to be dirty while NO field is
 * dirty — changes nothing the user made can account for. That is what makes it safe: a discard that left
 * real edits behind has entries in `dirtyFields`, so this does nothing and the footer keeps warning, which
 * is the correct outcome. A blunt "always re-baseline after discard" would bake unreverted input into the
 * saved baseline and turn a visible annoyance into silent data loss.
 *
 * Runs from an effect rather than inline because the value has to settle first: the reset triggers the
 * re-registration this exists to absorb, and child effects (the provider-config tabs) run before the
 * parent's.
 *
 * It also takes more than one pass. Each re-baseline hands back values that re-registration then rebuilds,
 * so the first pass does not converge — measured, not assumed: one pass left the form still claiming to be
 * dirty, and the retry below brought it to clean.
 */
export function useSettleDiscard<T extends FieldValues>(methods: UseFormReturn<T>): () => void {
    const [settling, setSettling] = useState(false);
    const [passes, setPasses] = useState(0);
    const { reset, getValues, formState } = methods;

    // Read during render so react-hook-form subscribes this component to both, and so the effect below
    // re-runs when either moves.
    const claimsDirty = formState.isDirty;
    const fieldIsDirty = Object.keys(formState.dirtyFields).length > 0;

    useEffect(() => {
        if (!settling) return;

        // A real edit survived the discard — leave it alone and keep warning. Correct, and the reason this
        // can never bake unsaved input into the baseline.
        if (fieldIsDirty) {
            setSettling(false);
            return;
        }

        // The reset clears isDirty for a moment before re-registration puts it back, so this waits for the
        // claim to actually appear rather than sampling once and missing it.
        if (!claimsDirty) return;

        // Bounded: if re-baselining has not settled the form in a few passes it is not going to, and a
        // stuck-on-Cancel footer is far better than a render loop.
        if (passes >= 3) { setSettling(false); return; }

        // The values are already right — it is the BASELINE that is wrong, so hand the current values back
        // as the new defaults.
        reset(getValues());
        setPasses((n) => n + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settling, claimsDirty, fieldIsDirty, passes]);

    return useCallback(() => { setPasses(0); setSettling(true); }, []);
}
